import { FC, useEffect, useState, useRef } from "react";
import styled from "styled-components";

import SearchBar from "@/components/SearchBar";
import useDebounce from "@/hooks/useDebounce";
import { Link } from "react-router-dom";
import Text from "@/components/Text";
import { api } from "@/utils/api";
import {
  OptimisationProjectNetworkEnum,
  OptimisationProjectTypeEnum,
} from "@/utils/enums";
import useAlerts from "@/hooks/useAlerts";
import { splitMatchedText } from "@/utils/helpers";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { Spinner } from "@/components/Button";

const StyledSearchBar = styled(SearchBar)`
  margin: 0;
`;

const Wrap = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  box-shadow: 0px 4px 8px rgba(37, 40, 43, 0.04);
  border-radius: 4px;
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  z-index: 2;
`;

const StyledLink = styled(Link)`
  display: block;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  transition: 0.2s ease-out background-color;

  &:hover {
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border: 0;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const StyledText = styled(Text)`
  > span {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: calc(50% - 9px);
  right: 0;
  margin-right: 16px;
  color: ${({ theme }) => theme.colors.purple};
`;

interface Project {
  id: number;
  title: string;
}

interface Props {
  teamId: string;
  network: OptimisationProjectNetworkEnum;
}

const OptimisationProjectsSearch: FC<Props> = ({ network, teamId }) => {
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const alerts = useAlerts();

  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => setProjects([]));

  const [projects, setProjects] = useState<Project[]>([]);

  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    if (debouncedSearch.length < 3) {
      setIsLoading(false);
      setProjects([]);
      return;
    }

    setIsLoading(true);

    api
      .get<never, Project[]>(`/v1/optimisation-tests/search`, {
        params: {
          "filter[team_id]": teamId,
          "filter[type]": OptimisationProjectTypeEnum.OWN,
          "filter[network]": network,
          "filter[search]": debouncedSearch,
        },
      })
      .then((results) => {
        setProjects(results);
        setIsLoading(false);
      })
      .catch(() => {
        alerts.error();
        setIsLoading(false);
      });
  }, [debouncedSearch, teamId, network, alerts]);

  return (
    <Wrap>
      <StyledSearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by Project ID, Name or Owner"
      />
      {isLoading && <StyledSpinner />}
      {!!projects.length && (
        <Dropdown ref={ref}>
          {projects.map((project) => (
            <StyledLink to={project.id.toString()} key={project.id}>
              <StyledText
                as="span"
                $type="smallHeadline"
                $color="gray"
                dangerouslySetInnerHTML={{
                  __html: splitMatchedText(project.title, debouncedSearch),
                }}
              />
            </StyledLink>
          ))}
        </Dropdown>
      )}
    </Wrap>
  );
};

export default OptimisationProjectsSearch;
